import { createContext, useContext, useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "./api";

// Multi-tenant Notify: each user can be granted multiple Notify-enabled
// domains. The active domain scopes the dashboard quota widget, the
// compose-time sender, and (via the GET /api/quota?domain=...) the
// Brevo credit display.
//
// Selection persists in localStorage so opening a new tab reuses the
// last choice. Single-domain users get auto-selected; zero-domain users
// get null (UI shows an empty-state nudge to contact an admin).

export interface UserDomain {
  notifyDomainId: string;
  domainName: string;
  senderDomain: string;
  senderLocalPart: string;
}

interface MeWithDomains {
  user: { id: string; email: string; name: string; role: string; senderLocalPart: string };
  domains: UserDomain[];
}

interface DomainCtxValue {
  domains: UserDomain[];
  activeId: string | null;
  active: UserDomain | null;
  setActiveId: (id: string) => void;
  loading: boolean;
}

const Ctx = createContext<DomainCtxValue | null>(null);
const STORAGE_KEY = "notify-active-domain";

export function DomainProvider({ children }: { children: React.ReactNode }) {
  const { data, isLoading } = useQuery({
    queryKey: ["me"],
    queryFn: () => api<MeWithDomains>("/api/auth/me"),
    staleTime: 60_000,
  });

  const [activeId, setActiveIdState] = useState<string | null>(() => {
    try { return localStorage.getItem(STORAGE_KEY); } catch { return null; }
  });

  // Reconcile the cached id with what /me returned. Three cases:
  //   1. cached id is still in the list → keep it
  //   2. cached id was removed / suspended → fall back to the first granted domain
  //   3. user has no domains → activeId = null
  useEffect(() => {
    if (!data) return;
    const ids = data.domains.map((d) => d.notifyDomainId);
    if (activeId && ids.includes(activeId)) return; // case 1
    const next = ids[0] ?? null;
    setActiveIdState(next);
    if (next) {
      try { localStorage.setItem(STORAGE_KEY, next); } catch { /* private mode */ }
    } else {
      try { localStorage.removeItem(STORAGE_KEY); } catch { /* */ }
    }
  }, [data, activeId]);

  function setActiveId(id: string) {
    setActiveIdState(id);
    try { localStorage.setItem(STORAGE_KEY, id); } catch { /* private mode */ }
  }

  const domains = data?.domains ?? [];
  const active = domains.find((d) => d.notifyDomainId === activeId) ?? null;

  return (
    <Ctx.Provider value={{ domains, activeId, active, setActiveId, loading: isLoading }}>
      {children}
    </Ctx.Provider>
  );
}

export function useDomains(): DomainCtxValue {
  const v = useContext(Ctx);
  if (!v) throw new Error("useDomains must be used inside <DomainProvider>");
  return v;
}
